import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  CapacityPlanTab,
  capacityPlanTabs,
  defaultCapacityPlanTab,
} from './CapacityPlanTab';

const agencyParam = 'Agency';
const runParam = 'Run';
const tabParam = 'Tab';
const newRunParam = 'New';

/**
 * `?Agency=network` stands for the country's Network Runs, which have no
 * Agency at all: the queries and the upload send `agencyId = null` for it.
 */
export const networkAgencyValue = 'network';

export interface CapacityPlanUrl {
  /** Raw `Agency` param: a Guid, `network`, or null when none is selected. */
  agencySelection: string | null;
  /** Guid of the selected Agency; null on a Network selection and on none. */
  agencyId: string | null;
  /** True while `?Agency=network` is selected. */
  isNetworkRun: boolean;
  /** Guid of the open Run, or null when the runs list is shown. */
  runId: string | null;
  tab: CapacityPlanTab;
  /** True while the "New run" form is open. */
  isNewRun: boolean;
  /** Takes a Guid, `networkAgencyValue`, or null to clear the selection. */
  selectAgency: (agency: string | null) => void;
  openRun: (runId: string, tab?: CapacityPlanTab) => void;
  setTab: (tab: CapacityPlanTab) => void;
  openNewRun: () => void;
  backToRuns: () => void;
}

const asTab = (value: string | null): CapacityPlanTab =>
  capacityPlanTabs.find((tab) => tab === value) ?? defaultCapacityPlanTab;

/**
 * The URL is the source of truth for capacity planning navigation:
 * `?Agency=<guid>&Run=<guid>&Tab=deliveries|result|settings` plus `&New=1`
 * for the "New run" form. Nothing here ever carries customer data.
 */
const useCapacityPlanUrl = (): CapacityPlanUrl => {
  const navigate = useNavigate();
  const { pathname, search } = useLocation();

  // Every write starts from the freshest params rather than from the ones this
  // render closed over: an awaited mutation calls back a tick later, and two
  // navigations can even be chained inside one tick (compute -> open the Result
  // tab), where the second must build on the first instead of undoing it.
  const locationRef = useRef({ pathname, search });
  if (locationRef.current.search !== search) {
    locationRef.current = { pathname, search };
  } else {
    locationRef.current.pathname = pathname;
  }

  const write = useCallback(
    (updated: Record<string, string | null>) => {
      const params = new URLSearchParams(locationRef.current.search);
      Object.entries(updated).forEach(([key, value]) => {
        if (value === null) {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      });
      const nextSearch = params.toString();
      locationRef.current = {
        pathname: locationRef.current.pathname,
        search: `?${nextSearch}`,
      };
      navigate({ pathname: locationRef.current.pathname, search: nextSearch });
    },
    [navigate],
  );

  const params = new URLSearchParams(search);
  const rawTab = params.get(tabParam);
  const tab = asTab(rawTab);
  const agencySelection = params.get(agencyParam);
  const isNetworkRun = agencySelection === networkAgencyValue;

  // A hand-edited or outdated `Tab` would otherwise show one tab and share
  // another, so the URL is rewritten to the tab the supervisor is looking at.
  useEffect(() => {
    if (rawTab !== null && rawTab !== tab) {
      write({ [tabParam]: tab });
    }
  }, [rawTab, tab, write]);

  return {
    agencySelection,
    agencyId: isNetworkRun ? null : agencySelection,
    isNetworkRun,
    runId: params.get(runParam),
    tab,
    isNewRun: params.get(newRunParam) === '1',
    // A Run belongs to an Agency, so a new Agency drops the whole selection.
    selectAgency: (nextAgency) =>
      write({
        [agencyParam]: nextAgency,
        [runParam]: null,
        [tabParam]: null,
        [newRunParam]: null,
      }),
    openRun: (nextRunId, nextTab = defaultCapacityPlanTab) =>
      write({
        [runParam]: nextRunId,
        [tabParam]: nextTab,
        [newRunParam]: null,
      }),
    setTab: (nextTab) => write({ [tabParam]: nextTab }),
    openNewRun: () =>
      write({
        [newRunParam]: '1',
        [runParam]: null,
        [tabParam]: null,
      }),
    backToRuns: () =>
      write({
        [runParam]: null,
        [tabParam]: null,
        [newRunParam]: null,
      }),
  };
};

export default useCapacityPlanUrl;
